import { useContext, useState } from 'react';
import { Alert, Button, Text } from '@backstage/ui';
import { useEntity } from '@backstage/plugin-catalog-react';
import {
  AccountEntity,
  filterMutedScanErrors,
} from '@internal/backstage-plugin-catalog-devops-common';
import { ModernRightDrawer } from '@internal/backstage-plugin-devops-react';
import { AccountContext } from '../Providers/AccountProvider';
import { ScanErrorsList } from '../ErrorDrawer/ScanErrorsList';
import styles from '../resources-page.module.css';

/**
 * Compact sync warning for the account resources tab: an Alert whose
 * "View details" opens a right drawer with the mockup's error cards (scope
 * badges, messages, admin mute/unmute, re-scan). Muted errors don't trigger
 * the warning tone. Replaces the old inline error card at the bottom of the
 * tab.
 */
export function ResourceErrorsAlert() {
  const { entity } = useEntity();
  // Raw useContext (not useAccountContext): this alert also renders on
  // project/rule pages, where no AccountProvider is mounted — there is
  // simply no scan flow there.
  const accountContext = useContext(AccountContext);
  const account = entity as AccountEntity;
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const errors = account.spec?.errors ?? [];
  if (!errors.length) return null;

  const mutedErrors = account.spec?.mutedErrors ?? [];
  const unmutedErrors = filterMutedScanErrors(errors, mutedErrors);
  const mutedCount = errors.length - unmutedErrors.length;

  const allMuted = unmutedErrors.length === 0;

  return (
    <>
      <Alert
        status={allMuted ? 'info' : 'warning'}
        icon
        title={
          allMuted
            ? `Scan errors are muted (${mutedCount})`
            : 'Not all rules or collectors were executed, possible issues with permission'
        }
        description={
          <Text>
            The last scan reported {errors.length}{' '}
            {errors.length === 1 ? 'error' : 'errors'}
            {allMuted ? '. Muted errors are excluded from the warnings.' : '.'}
          </Text>
        }
        customActions={
          <Button
            variant="secondary"
            size="small"
            onPress={() => setIsDetailsOpen(true)}
          >
            View details
          </Button>
        }
      />
      <ModernRightDrawer
        title="Failed resources"
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        width={580}
      >
        <Text as="p" className={styles.kicker} style={{ marginBottom: '4px' }}>
          Inventory sync
        </Text>
        <ScanErrorsList
          onStartScan={
            accountContext
              ? () => {
                  setIsDetailsOpen(false);
                  accountContext.startScanButtonClick();
                }
              : undefined
          }
        />
      </ModernRightDrawer>
    </>
  );
}
